import { useState } from "react";
import { styled } from "@pigment-css/react";
import { useTranslations } from "@acto/web-common/locales";
import { WorkflowActivity } from "@acto/taskpane/core/domain";
import dayjs from "dayjs";

const PAGE_SIZE = 5;

interface WorkflowActivitiesListProps {
  activities: WorkflowActivity[];
  locale: string;
}

const SectionContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.managementView.spacing[32],
}));

const HeaderRow = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
});

const HeaderTitle = styled("p")(({ theme }) => ({
  fontFamily: theme.managementView.font.family,
  fontSize: theme.managementView.font.size[16],
  fontWeight: theme.managementView.font.weight[500],
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  color: theme.managementView.colors.neutral[90],
  margin: 0,
}));

const TimelineContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  paddingLeft: "6px",
});

const TimelineList = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
});

const DateRowContainer = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: "13px",
});

const DateIconWrapper = styled("div")({
  width: "28px",
  height: "24px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  position: "relative",
});

const SmallDot = styled("div")(({ theme }) => ({
  height: "6px",
  width: "6px",
  borderRadius: "50%",
  backgroundColor: "transparent",
  border: "3px solid " + theme.managementView.colors.neutral[8],
}));

const BiggerDot = styled("div")(({ theme }) => ({
  height: "10px",
  width: "10px",
  borderRadius: "50%",
  backgroundColor: theme.managementView.colors.neutral[8],
}));

const DateBadge = styled("div")(({ theme }) => ({
  backgroundColor: theme.managementView.colors.neutral[4],
  padding: `${theme.managementView.spacing[4]} ${theme.managementView.spacing[10]}`,
  borderRadius: "100px",
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[500],
  color: theme.managementView.colors.neutral[56],
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  lineHeight: "21px",
}));

const ActivityGroupContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  position: "relative",
});

const ActivityGroupHeader = styled("div")({
  display: "flex",
  alignItems: "flex-start",
  gap: "13px",
  marginBottom: "12px",
});

const ActivityGroupItems = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  marginLeft: "6px",
  position: "relative",
});

// fixed color used because the curved connector visually overlaps which looks terrible with opacity
// same color used as in the field-sales widget
const VerticalLineFromDot = styled("div")(() => ({
  // TODO: the height needs to be 8px less than it is currently
  position: "absolute",
  height: "calc(100% - 44px)",
  left: "8px",
  top: "-16px",
  width: "1px",
  backgroundColor: "#DFDFDF",
  zIndex: 0,
}));

const CurvedConnector = styled("div")(() => ({
  position: "absolute",
  left: "8px",
  top: "0px",
  width: "8px",
  height: "8px",
  borderLeft: `1px solid #DFDFDF`,
  borderBottom: `1px solid #DFDFDF`,
  borderBottomLeftRadius: "16px",
  boxSizing: "border-box",
  zIndex: 1,
}));

const GroupedActivityRow = styled("div")({
  display: "flex",
  alignItems: "flex-start",
  gap: "8px",
  position: "relative",
  paddingLeft: "32px",
  minHeight: "60px",
});

const CheckboxCircle = styled("div")(({ theme }) => ({
  width: "18px",
  height: "18px",
  borderRadius: "50%",
  border: `2px solid ${theme.managementView.colors.neutral[24]}`,
  backgroundColor: theme.managementView.colors.neutral[0],
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "10px",
  color: theme.managementView.colors.neutral[0],
  flexShrink: 0,
  position: "relative",
  zIndex: 1,
  '&[data-completed="true"]': {
    border: `2px solid ${theme.managementView.colors.success[100]}`,
    backgroundColor: theme.managementView.colors.success[100],
  },
}));

const ActivityContentWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.managementView.spacing[4],
  flex: 1,
}));

const CustomerNameText = styled("p")(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[15],
  fontWeight: theme.managementView.font.weight[500],
  color: theme.managementView.colors.neutral[90],
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  lineHeight: "21px",
  margin: 0,
}));

const CustomerExternalRefText = styled("span")(({ theme }) => ({
  color: theme.managementView.colors.neutral[56],
}));

const EmployeeText = styled("p")(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[500],
  color: theme.managementView.colors.neutral[56],
  margin: 0,
  lineHeight: "23px",
}));

const UnassignedText = styled(EmployeeText)({
  fontStyle: "italic",
});

const ShowMoreContainer = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: "13px",
});

const ShowMoreButton = styled("button")(({ theme }) => ({
  backgroundColor: theme.managementView.colors.neutral[4],
  padding: `${theme.managementView.spacing[4]} ${theme.managementView.spacing[10]}`,
  borderRadius: "100px",
  fontFamily: theme.managementView.font.familySecondary,
  fontWeight: theme.managementView.font.weight[500],
  color: theme.managementView.colors.neutral[56],
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  border: "none",
  cursor: "pointer",
  transition: "background-color 0.2s",
  "&:hover": {
    backgroundColor: theme.managementView.colors.neutral[8],
  },
}));

const EmptyState = styled("p")(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[400],
  color: theme.managementView.colors.neutral[64],
  textAlign: "center",
  padding: theme.managementView.spacing[24],
  margin: 0,
}));

const ActivityRow = ({ activity }: { activity: WorkflowActivity }) => {
  const t = useTranslations("managementView.workflowDetails");

  const showExternalRefSuffix =
    activity.customerExternalReference !== "" && activity.customerExternalReference !== activity.customerName;

  return (
    <GroupedActivityRow>
      <CurvedConnector />
      <CheckboxCircle data-completed={activity.isCompleted}>{activity.isCompleted && "✓"}</CheckboxCircle>
      <ActivityContentWrapper>
        <CustomerNameText>
          {activity.customerName}
          {showExternalRefSuffix && (
            <>
              {" "}
              <CustomerExternalRefText>{activity.customerExternalReference}</CustomerExternalRefText>
            </>
          )}
        </CustomerNameText>
        {activity.assignedEmployee ? (
          <EmployeeText>{activity.assignedEmployee.name}</EmployeeText>
        ) : (
          <UnassignedText>{t("unassigned")}</UnassignedText>
        )}
      </ActivityContentWrapper>
    </GroupedActivityRow>
  );
};

export const WorkflowActivitiesList = ({ activities, locale }: WorkflowActivitiesListProps) => {
  const t = useTranslations("managementView.workflowDetails");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const sortedActivities = [...activities].sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf());

  if (activities.length === 0) {
    return (
      <SectionContainer>
        <HeaderRow>
          <HeaderTitle>{t("activity")}</HeaderTitle>
        </HeaderRow>
        <EmptyState>{t("noActivities")}</EmptyState>
      </SectionContainer>
    );
  }

  const visibleActivities = sortedActivities.slice(0, visibleCount);
  const hasMore = visibleCount < sortedActivities.length;

  // Group visible activities by date
  const groupedActivities = visibleActivities.reduce(
    (acc, activity) => {
      const dateKey = activity.date;
      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }
      acc[dateKey].push(activity);
      return acc;
    },
    {} as Record<string, WorkflowActivity[]>,
  );

  // Sort dates descending
  const sortedDates = Object.keys(groupedActivities).sort((a, b) => dayjs(b).valueOf() - dayjs(a).valueOf());

  // Flatten all activities with metadata for rendering
  const flatActivities: {
    type: "date" | "activity" | "activity-group";
    date?: string;
    activity?: WorkflowActivity;
    activities?: WorkflowActivity[];
  }[] = [];

  sortedDates.forEach((date) => {
    flatActivities.push({ type: "date", date });

    const dateActivities = groupedActivities[date];

    if (dateActivities.length > 1) {
      flatActivities.push({ type: "activity-group", activities: dateActivities });
    } else {
      flatActivities.push({ type: "activity", activity: dateActivities[0] });
    }
  });

  return (
    <SectionContainer>
      <HeaderRow>
        <HeaderTitle>{t("activity")}</HeaderTitle>
      </HeaderRow>
      <TimelineContainer>
        <TimelineList>
          {flatActivities.map((item, index) => {
            if (item.type === "date") {
              return (
                <DateRowContainer key={`date-${item.date}`}>
                  <DateIconWrapper>
                    <SmallDot />
                  </DateIconWrapper>
                  <DateBadge>{dayjs(item.date).locale(locale).format("DD. MMM")}</DateBadge>
                </DateRowContainer>
              );
            }

            if (item.type === "activity-group" && item.activities) {
              return (
                <ActivityGroupContainer key={`group-${index}`}>
                  <ActivityGroupHeader>
                    <DateIconWrapper>
                      <BiggerDot />
                    </DateIconWrapper>
                  </ActivityGroupHeader>
                  <ActivityGroupItems>
                    {item.activities.length > 1 && <VerticalLineFromDot />}
                    {item.activities.map((activity, activityIndex) => (
                      <ActivityRow key={`grouped-activity-${index}-${activityIndex}`} activity={activity} />
                    ))}
                  </ActivityGroupItems>
                </ActivityGroupContainer>
              );
            }

            if (!item.activity) return null;

            // Single activity — wrap in ActivityGroupContainer so connector lines
            // are rendered the same way as in grouped entries
            return (
              <ActivityGroupContainer key={`activity-${index}`}>
                <ActivityGroupHeader>
                  <DateIconWrapper>
                    <BiggerDot />
                  </DateIconWrapper>
                </ActivityGroupHeader>
                <ActivityGroupItems>
                  <ActivityRow activity={item.activity} />
                </ActivityGroupItems>
              </ActivityGroupContainer>
            );
          })}
          {hasMore && (
            <ShowMoreContainer>
              <DateIconWrapper>
                <SmallDot />
              </DateIconWrapper>
              <ShowMoreButton onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}>{t("showMore")}</ShowMoreButton>
            </ShowMoreContainer>
          )}
        </TimelineList>
      </TimelineContainer>
    </SectionContainer>
  );
};
